/**
 * Attribute Boxes
 * Zamienia selecty WooCommerce na boxy
 */

// Import stylu
import './style.scss';

class AttributeBoxes {
    constructor() {
        this.debug = true;
        this.init();
    }

    init() {
        this.bindEvents();
        this.initializeExistingSelections();
    }

    bindEvents() {
        // Sprawdź czy jesteśmy na stronie produktu
        if (!document.querySelector('.variations_form')) {
            return;
        }

        this.log('Initializing Attribute Boxes');

        // Synchronizuj istniejące wybory po załadowaniu
        this.initializeExistingSelections();

        // Obsługa kliknięć w boxy
        document.addEventListener('click', (e) => {
            if (e.target.matches('.acv-attribute-box:not(.disabled)')) {
                this.handleBoxClick(e);
            }
        });

        // Obsługa zmian w selectach
        document.addEventListener('change', (e) => {
            if (e.target.matches('.variations_form select[data-attribute_name]')) {
                this.handleSelectChange(e);
            }
        });

        // Nasłuchuj na WooCommerce eventy
        document.body.addEventListener('woocommerce_update_variation_values', () => {
            this.updateAvailableOptions();
        });

        // Re-initialize po AJAX
        document.body.addEventListener('updated_wc_div', () => {
            setTimeout(() => {
                this.updateAvailableOptions();
                this.initializeExistingSelections(); // Re-sync po AJAX
            }, 100);
        });

        this.log('Events bound successfully');
    }

    handleBoxClick(event) {
        event.preventDefault();
        
        const box = event.target;
        const container = box.closest('.acv-attribute-boxes');
        const select = container.querySelector('select');
        
        if (!select) {
            this.log('Error: Select not found', 'error');
            return;
        }

        const value = box.dataset.value;
        const currentValue = select.value;
        
        this.log(`Box clicked: ${value}, current: ${currentValue}`);
        
        // Usuń selected z wszystkich boksów w kontenerze
        container.querySelectorAll('.acv-attribute-box').forEach(b => {
            b.classList.remove('selected', 'from-url');
        });
        
        // Toggle selection
        if (currentValue === value) {
            // Deselect
            select.value = '';
            this.triggerChangeEvent(select);
        } else {
            // Select
            box.classList.add('selected');
            select.value = value;
            this.triggerChangeEvent(select);
            
            this.updateUrlWithSelection();
        }
    }

    handleSelectChange(event) {
        const select = event.target;
        const attributeName = select.dataset.attribute_name;
        
        if (!attributeName) return;
        
        const cleanAttributeName = attributeName.replace('attribute_', '');
        const container = document.querySelector(`[data-attribute="${cleanAttributeName}"]`);
        
        if (!container) return;
        
        const selectedValue = select.value;
        this.log(`Select changed: ${cleanAttributeName} = ${selectedValue}`);
        
        // Aktualizuj boxy
        container.querySelectorAll('.acv-attribute-box').forEach(box => {
            if (box.dataset.value === selectedValue && selectedValue !== '') {
                box.classList.add('selected');
            } else {
                box.classList.remove('selected');
            }
        });
    }

    updateAvailableOptions() {
        this.log('Updating available options');
        
        const containers = document.querySelectorAll('.acv-attribute-boxes');
        
        containers.forEach(container => {
            const select = container.querySelector('select');
            if (!select) return;
            
            // Pobierz dostępne opcje
            const availableOptions = [];
            select.querySelectorAll('option').forEach(option => {
                const value = option.value;
                if (value && !option.disabled) {
                    availableOptions.push(value);
                }
            });
            
            this.log(`Available options:`, availableOptions);
            
            // Aktualizuj boxy
            container.querySelectorAll('.acv-attribute-box').forEach(box => {
                const value = box.dataset.value;
                
                if (availableOptions.includes(value)) {
                    box.classList.remove('disabled');
                } else {
                    box.classList.add('disabled');
                    box.classList.remove('selected');
                    
                    // Jeśli aktualnie wybrany box stał się niedostępny
                    if (select.value === value) {
                        select.value = '';
                        this.triggerChangeEvent(select);
                    }
                }
            });
        });
    }

    // Inicjalizuje istniejące wybory (dla defaultowych selekcji)
    initializeExistingSelections() {
        this.log('🔄 Initializing existing selections...');
        
        const containers = document.querySelectorAll('.acv-attribute-boxes');
        let foundDefaults = 0;
        
        containers.forEach(container => {
            const select = container.querySelector('select');
            if (!select) {
                this.log('❌ No select found in container');
                return;
            }
            
            const attributeName = select.dataset.attribute_name || 'unknown';
            const cleanAttributeName = attributeName.replace('attribute_', '');
            let selectedValue = select.value;
            
            // Sprawdź URL przed defaultami
            const urlValue = this.getUrlParameter(cleanAttributeName);
            if (urlValue) {
                const option = select.querySelector(`option[value="${urlValue}"]`);
                if (option && !option.disabled) {
                    selectedValue = urlValue;
                    this.log(`🌐 Using URL value: ${cleanAttributeName} = ${selectedValue}`);
                }
            }
            
            // Metoda 1: Sprawdź selected option
            if (!selectedValue) {
                const selectedOption = select.querySelector('option[selected]');
                if (selectedOption) {
                    selectedValue = selectedOption.value;
                    this.log(`📍 Found selected option: ${selectedValue}`);
                }
            }
            
            // Metoda 2: Sprawdź first non-empty option jako fallback
            if (!selectedValue) {
                const firstOption = select.querySelector('option[value]:not([value=""])');
                if (firstOption) {
                    selectedValue = firstOption.value;
                    this.log(`📍 Using first option as default: ${selectedValue}`);
                }
            }
            
            // Metoda 3: Sprawdź data attributy na produkcie
            if (!selectedValue) {
                const form = select.closest('form.variations_form');
                if (form) {
                    const defaultVariation = form.dataset.default_variation;
                    if (defaultVariation) {
                        try {
                            const defaults = JSON.parse(defaultVariation);
                            const attrKey = attributeName.replace('attribute_', '');
                            selectedValue = defaults[attrKey] || defaults[attributeName];
                            this.log(`📍 Found in default_variation: ${selectedValue}`);
                        } catch(e) {
                            this.log('❌ Error parsing default_variation');
                        }
                    }
                }
            }
            
            this.log(`🔍 Final check - attribute: ${cleanAttributeName}, value: "${selectedValue}"`);
            
            if (!selectedValue) {
                this.log(`⚪ No default value found for ${cleanAttributeName}`);
                return;
            }
            
            const selectedBox = container.querySelector(`[data-value="${selectedValue}"]`);
            
            if (selectedBox) {
                // Usuń selected z wszystkich boksów
                container.querySelectorAll('.acv-attribute-box').forEach(box => {
                    box.classList.remove('selected');
                });
                
                // Dodaj selected do właściwego boxa
                selectedBox.classList.add('selected');
                
                // Ustaw też wartość w select
                select.value = selectedValue;
                
                foundDefaults++;
                this.log(`✅ Initialized selection: ${cleanAttributeName} = ${selectedValue}`);
            } else {
                this.log(`❌ Box not found for value: ${selectedValue}`);
                // Debug: pokaż dostępne opcje
                const availableBoxes = container.querySelectorAll('.acv-attribute-box');
                this.log(`Available boxes:`, Array.from(availableBoxes).map(b => b.dataset.value));
                
                // Debug: pokaż wszystkie opcje w select
                const allOptions = select.querySelectorAll('option');
                this.log(`Select options:`, Array.from(allOptions).map(o => ({value: o.value, text: o.textContent, selected: o.selected})));
            }
        });
        
        this.log(`🎯 Initialized ${foundDefaults} default selections`);
    }

    /**
     * Pobiera parametry z URL
     */
    getUrlParameters() {
        const urlParams = new URLSearchParams(window.location.search);
        const params = {};
        
        for (const [key, value] of urlParams) {
            params[key.toLocaleLowerCase()] = decodeURIComponent(value);
        }
        
        return params;
    }

    /**
     * Wywołuje aktualizację formularza wariantów
     */
    triggerVariationFormUpdate() {
        const form = document.querySelector('.variations_form');
        if (form) {
            // Poczekaj chwilę przed triggerem, aby DOM był gotowy
            setTimeout(() => {
                // Sprawdź czy jQuery jest dostępne
                if (typeof jQuery !== 'undefined') {
                    // Wywołaj zdarzenie WooCommerce
                    jQuery(form).trigger('woocommerce_variation_select_change');
                    jQuery(form).trigger('check_variations');
                }
                
                this.log('🔄 Triggered variation form update');
            }, 100);
        }
    }

    /**
     * Aktualizuje URL z aktualnie wybranymi wartościami
     */
    updateUrlWithSelection() {
        const selectedValues = this.getSelectedValues();
        const url = new URL(window.location);
        
        // Usuń istniejące parametry atrybutów
        for (const key of url.searchParams.keys()) {
            if (key.startsWith('attribute_')) {
                url.searchParams.delete(key);
            }
        }
        
        // Dodaj nowe wartości
        for (const [attribute, value] of Object.entries(selectedValues)) {
            if (value) {
                url.searchParams.set(`attribute_${attribute.toLocaleLowerCase()}`, value);
            }
        }
        
        // Aktualizuj URL bez przeładowania strony
        window.history.replaceState({}, '', url.toString());
        
        this.log('🔗 Updated URL with selections:', selectedValues);
    }

    /**
     * Sprawdza czy są jakiekolwiek parametry attribute_* w URL
     */
    hasAnyUrlParameters() {
        const urlParams = new URLSearchParams(window.location.search);
        for (const key of urlParams.keys()) {
            if (key.startsWith('attribute_')) {
                return true;
            }
        }
        return false;
    }

    triggerChangeEvent(element) {
        // Trigger native change event
        const event = new Event('change', {
            bubbles: true,
            cancelable: true
        });
        element.dispatchEvent(event);
    }

    log(message, type = 'info') {
        if (this.debug && console[type]) {
            console[type](`[Attribute Boxes] ${message}`, typeof message === 'object' ? message : '');
        }
    }

    // Public API
    getSelectedValues() {
        const values = {};
        document.querySelectorAll('.acv-attribute-boxes').forEach(container => {
            const attribute = container.dataset.attribute;
            const select = container.querySelector('select');
            if (select) {
                values[attribute] = select.value;
            }
        });
        return values;
    }

    setAttributeValue(attribute, value) {
        const container = document.querySelector(`[data-attribute="${attribute}"]`);
        if (!container) return false;
        
        const select = container.querySelector('select');
        const box = container.querySelector(`[data-value="${value}"]`);
        
        if (select && box && !box.classList.contains('disabled')) {
            // Usuń selected z innych boksów
            container.querySelectorAll('.acv-attribute-box').forEach(b => {
                b.classList.remove('selected');
            });
            
            // Ustaw wartość
            select.value = value;
            box.classList.add('selected');
            
            // Trigger change bez aktualizacji URL
            this.triggerChangeEvent(select);
            return true;
        }
        
        return false;
    }

    clearAttribute(attribute) {
        return this.setAttributeValue(attribute, '');
    }

    clearAllAttributes() {
        document.querySelectorAll('.acv-attribute-boxes select').forEach(select => {
            select.value = '';
            this.triggerChangeEvent(select);
        });
    }

    /**
     * Sprawdza czy dany atrybut ma wartość z URL
     */
    hasUrlParameter(attribute) {
        const urlParams = this.getUrlParameters();
        return urlParams.hasOwnProperty(`attribute_${attribute}`);
    }

    /**
     * Pobiera wartość atrybutu z URL
     */
    getUrlParameter(attribute) {
        const urlParams = this.getUrlParameters();

        return urlParams[`attribute_${attribute}`] || null;
    }

    /**
     * Włącza/wyłącza debug mode
     */
    setDebugMode(enabled) {
        this.debug = enabled;
        this.log(`Debug mode ${enabled ? 'enabled' : 'disabled'}`);
    }
}

// Initialize
const attributeBoxes = new AttributeBoxes();

// Export for external use
window.AttributeBoxes = attributeBoxes;